import { Link } from 'react-router-dom';
import { useState, useEffect } from 'react';
import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';
import Button from '../../components/base/Button';

export default function Home() {
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    setIsVisible(true);
    
    // Update page title and meta description
    document.title = 'Regnskapsfører Oslo | Regnskap & Bokføring | Easy Accounting AS';
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute('content', 'Easy Accounting AS – ledende regnskapskontor i Oslo. Vi tilbyr profesjonell regnskapsføring, bokføring, lønn, MVA og rådgivning. Statsautoriserte regnskapsførere med 15+ års erfaring. Få gratis tilbud i dag!');
    }
    
    // Add canonical link
    let canonicalLink = document.querySelector('link[rel="canonical"]');
    if (!canonicalLink) {
      canonicalLink = document.createElement('link');
      canonicalLink.setAttribute('rel', 'canonical');
      document.head.appendChild(canonicalLink);
    }
    canonicalLink.setAttribute('href', 'https://easy.no/');
    
    // Add Schema.org JSON-LD
    const schemaScript = document.createElement('script');
    schemaScript.type = 'application/ld+json';
    schemaScript.textContent = JSON.stringify({
      "@context": "https://schema.org",
      "@type": ["AccountingService", "LocalBusiness"],
      "name": "Easy Accounting AS",
      "url": "https://easy.no",
      "logo": "https://easy.no/logo.png",
      "image": "https://easy.no/og-image.jpg",
      "telephone": "+4795487639",
      "email": "post@easya.no",
      "address": {
        "@type": "PostalAddress",
        "streetAddress": "Brynsveien 2",
        "postalCode": "0667",
        "addressLocality": "Oslo",
        "addressCountry": "NO"
      },
      "geo": {
        "@type": "GeoCoordinates",
        "latitude": "59.9",
        "longitude": "10.8"
      },
      "openingHoursSpecification": [
        {
          "@type": "OpeningHoursSpecification",
          "dayOfWeek": ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday"],
          "opens": "09:00",
          "closes": "17:00"
        }
      ],
      "priceRange": "$$",
      "areaServed": {
        "@type": "City",
        "name": "Oslo"
      },
      "serviceArea": {
        "@type": "Country",
        "name": "Norge"
      },
      "sameAs": [
        "https://www.facebook.com/EasyAccountingAS",
        "https://www.linkedin.com/company/easy-accounting-as"
      ],
      "aggregateRating": {
        "@type": "AggregateRating",
        "ratingValue": "5",
        "reviewCount": "500"
      }
    });
    document.head.appendChild(schemaScript);
    
    // Add BreadcrumbList schema
    const breadcrumbScript = document.createElement('script');
    breadcrumbScript.type = 'application/ld+json';
    breadcrumbScript.textContent = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      "itemListElement": [
        {
          "@type": "ListItem",
          "position": 1,
          "name": "Hjem",
          "item": "https://easy.no/"
        }
      ]
    });
    document.head.appendChild(breadcrumbScript);
    
    return () => {
      // Cleanup schema scripts on unmount
      const existingScripts = document.querySelectorAll('script[type="application/ld+json"]');
      existingScripts.forEach(script => {
        if (document.head.contains(script)) {
          document.head.removeChild(script);
        }
      });
    };
  }, []);

  const services = [
    {
      icon: 'ri-calculator-line',
      title: 'Bokføring',
      description: 'Komplett bokføringstjeneste tilpasset din bedrift. Vi håndterer alle daglige transaksjoner.'
    },
    {
      icon: 'ri-line-chart-line',
      title: 'Regnskap',
      description: 'Profesjonell regnskapsføring som gir deg full oversikt over bedriftens økonomi.'
    },
    {
      icon: 'ri-money-dollar-circle-line',
      title: 'Lønn',
      description: 'Lønnstjenester inkludert lønnsslipp, skattetrekk og rapportering til myndighetene.'
    },
    {
      icon: 'ri-file-chart-line',
      title: 'MVA-rapportering',
      description: 'Nøyaktig MVA-rapportering og innlevering til rett tid, hver gang.'
    },
    {
      icon: 'ri-folder-chart-line',
      title: 'Årsregnskap',
      description: 'Komplett årsregnskap utarbeidet i henhold til norske regnskapsstandarder.'
    },
    {
      icon: 'ri-building-line',
      title: 'Stiftelse av selskap',
      description: 'Hjelp med etablering av nye selskaper og all nødvendig dokumentasjon.'
    }
  ];

  const testimonials = [
    {
      name: 'Vidar Nilsen',
      company: 'Ing. Vidar Nilsen AS',
      text: 'Easy Accounting har gjort regnskapet vårt mye enklere. Fantastisk service og alltid tilgjengelige når vi trenger hjelp.'
    },
    {
      name: 'Sam Houlali',
      company: 'Bobben Spiseri og Vinbar AS',
      text: 'Profesjonell og pålitelig. De tar seg av alt av regnskapsarbeid slik at jeg kan fokusere på min forretning.'
    },
    {
      name: 'Nicholas John Roberts',
      company: 'Birdieboss AS',
      text: 'Utmerket kompetanse og konkurransedyktige priser. Kan anbefale Easy Accounting til alle bedrifter.'
    }
  ];

  return (
    <div className="min-h-screen bg-white">
      <Header />
      
      {/* Hero Section */}
      <section 
        className="relative bg-cover bg-center bg-no-repeat min-h-screen flex items-center overflow-hidden"
        style={{
          backgroundImage: `linear-gradient(rgba(0, 0, 0, 0.4), rgba(0, 0, 0, 0.4)), url(https://public.readdy.ai/ai/img_res/0f0524bca16a8556161f62f26b6d72c2.jpg)`
        }}
      >
        <div className="absolute inset-0 bg-gradient-to-r from-blue-900/90 to-blue-700/70"></div>
        
        {/* Floating animation elements */}
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute top-20 left-4 sm:left-10 w-16 sm:w-20 h-16 sm:h-20 bg-blue-400/10 rounded-full animate-bounce" style={{ animationDelay: '0s', animationDuration: '3s' }}></div>
          <div className="absolute top-40 right-4 sm:right-20 w-12 sm:w-16 h-12 sm:h-16 bg-white/10 rounded-full animate-bounce" style={{ animationDelay: '1s', animationDuration: '4s' }}></div>
          <div className="absolute bottom-40 left-4 sm:left-20 w-10 sm:w-12 h-10 sm:h-12 bg-blue-300/20 rounded-full animate-bounce" style={{ animationDelay: '2s', animationDuration: '5s' }}></div>
          <div className="absolute bottom-20 right-8 sm:right-40 w-16 sm:w-24 h-16 sm:h-24 bg-white/5 rounded-full animate-bounce" style={{ animationDelay: '0.5s', animationDuration: '3.5s' }}></div>
        </div>

        <div className="relative mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 w-full">
          <div className={`max-w-2xl transition-all duration-1000 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
            <h1 className="text-3xl sm:text-4xl lg:text-6xl font-bold tracking-tight text-white animate-fade-in-up">
              Regnskapsfører i Oslo – Din pålitelige regnskapspartner
            </h1>
            <p className={`mt-4 sm:mt-6 text-base sm:text-lg leading-7 sm:leading-8 text-gray-200 transition-all duration-1000 delay-3 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
              Easy Accounting AS leverer profesjonelle regnskaps- og bokføringstjenester til bedrifter i alle størrelser. La oss ta hånd om regnskapet ditt, så kan du fokusere på det du gjør best.
            </p>
            <div className={`mt-8 sm:mt-10 flex flex-col sm:flex-row items-center gap-4 sm:gap-x-6 transition-all duration-1000 delay-5 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
              <Link to="/kontakt" className="w-full sm:w-auto">
                <Button size="lg" className="w-full sm:w-auto hover:scale-105 transition-transform duration-300 whitespace-nowrap">
                  Få gratis tilbud
                </Button>
              </Link>
              <Link to="/tjenester" className="w-full sm:w-auto">
                <Button variant="outline" size="lg" className="w-full sm:w-auto text-white border-white hover:bg-white hover:text-blue-600 hover:scale-105 transition-all duration-300 whitespace-nowrap">
                  Se våre tjenester
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* SEO Content Section */}
      <section className="py-12 sm:py-16 bg-white">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="prose prose-lg max-w-none">
            <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-4">
              Profesjonelt regnskapskontor i Oslo
            </h2>
            <p className="text-base sm:text-lg text-gray-700 leading-relaxed mb-4">
              Som et ledende <strong>regnskapskontor i Oslo</strong>, tilbyr Easy Accounting AS omfattende <strong>regnskapsrådgivning</strong> og <strong>bokføring for bedrifter</strong> i alle størrelser. Våre <strong>statsautoriserte regnskapsførere</strong> har over 15 års erfaring med å levere nøyaktige og pålitelige regnskapstjenester.
            </p>
            <p className="text-base sm:text-lg text-gray-700 leading-relaxed">
              Vi spesialiserer oss på <strong>regnskapsføring</strong>, <strong>bokføring</strong>, <strong>lønn</strong>, <strong>MVA-rapportering</strong> og <strong>årsregnskap</strong>. Enten du driver et lite enkeltpersonforetak eller et større aksjeselskap, har vi kompetansen og erfaringen til å håndtere alle dine regnskapsbehov. Vårt <strong>regnskapsbyrå i Oslo</strong> er dedikert til å gi deg trygghet og full oversikt over bedriftens økonomi.
            </p>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section className="py-16 sm:py-24 bg-gray-50">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="mx-auto max-w-2xl text-center">
            <h2 className="text-2xl sm:3xl lg:4xl font-bold tracking-tight text-gray-900 animate-fade-in-up">
              Våre tjenester
            </h2>
            <p className="mt-3 sm:mt-4 text-base sm:text-lg leading-7 sm:leading-8 text-gray-600">
              Vi tilbyr et komplett spekter av regnskaps- og bokføringstjenester
            </p>
          </div>
          <div className="mx-auto mt-12 sm:mt-16 grid max-w-2xl grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 sm:gap-8 lg:max-w-none">
            {services.map((service, index) => (
              <div 
                key={index} 
                className="bg-white rounded-xl shadow-sm p-6 sm:p-8 hover:shadow-md transition-all duration-300 hover:-translate-y-1 hover:scale-105 animate-fade-in-up"
                style={{ animationDelay: `${index * 0.1}s` }}
              >
                <div className="w-10 sm:w-12 h-10 sm:h-12 flex items-center justify-center bg-blue-100 rounded-lg mb-3 sm:mb-4 animate-pulse">
                  <i className={`${service.icon} text-xl sm:text-2xl text-blue-600`}></i>
                </div>
                <h3 className="text-lg sm:text-xl font-semibold text-gray-900 mb-2 sm:mb-3">
                  {service.title}
                </h3>
                <p className="text-sm sm:text-base text-gray-600">
                  {service.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Why Choose Us Section */}
      <section className="py-16 sm:py-24 bg-white">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 sm:gap-12 items-center">
            <div className="animate-fade-in-left order-2 lg:order-1">
              <h2 className="text-2xl sm:3xl lg:4xl font-bold tracking-tight text-gray-900 mb-4 sm:mb-6">
                Hvorfor velge Easy Accounting?
              </h2>
              <div className="space-y-4 sm:space-y-6">
                <div className="flex items-start hover:bg-gray-50 p-3 sm:p-4 rounded-lg transition-colors duration-300">
                  <div className="w-6 sm:w-8 h-6 sm:h-8 flex items-center justify-center bg-green-100 rounded-full mr-3 sm:mr-4 mt-1 animate-pulse flex-shrink-0">
                    <i className="ri-check-line text-sm sm:text-base text-green-600"></i>
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-900 mb-1 sm:mb-2 text-sm sm:text-base">Over 15 års erfaring</h3>
                    <p className="text-gray-600 text-sm sm:text-base">Vi har hjulpet hundrevis av bedrifter med deres regnskap siden 2009.</p>
                  </div>
                </div>
                <div className="flex items-start hover:bg-gray-50 p-3 sm:p-4 rounded-lg transition-colors duration-300">
                  <div className="w-6 sm:w-8 h-6 sm:h-8 flex items-center justify-center bg-green-100 rounded-full mr-3 sm:mr-4 mt-1 animate-pulse flex-shrink-0">
                    <i className="ri-check-line text-sm sm:text-base text-green-600"></i>
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-900 mb-1 sm:mb-2 text-sm sm:text-base">Personlig service</h3>
                    <p className="text-gray-600 text-sm sm:text-base">Du får en fast kontaktperson som kjenner din bedrift og dine behov.</p>
                  </div>
                </div>
                <div className="flex items-start hover:bg-gray-50 p-3 sm:p-4 rounded-lg transition-colors duration-300">
                  <div className="w-6 sm:w-8 h-6 sm:h-8 flex items-center justify-center bg-green-100 rounded-full mr-3 sm:mr-4 mt-1 animate-pulse flex-shrink-0">
                    <i className="ri-check-line text-sm sm:text-base text-green-600"></i>
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-900 mb-1 sm:mb-2 text-sm sm:text-base">Moderne teknologi</h3>
                    <p className="text-gray-600 text-sm sm:text-base">Vi bruker de nyeste regnskapssystemene for effektiv og sikker behandling.</p>
                  </div>
                </div>
                <div className="flex items-start hover-bg-gray-50 p-3 sm:p-4 rounded-lg transition-colors duration-300">
                  <div className="w-6 sm:w-8 h-6 sm:h-8 flex items-center justify-center bg-green-100 rounded-full mr-3 sm:mr-4 mt-1 animate-pulse flex-shrink-0">
                    <i className="ri-check-line text-sm sm:text-base text-green-600"></i>
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-900 mb-1 sm:mb-2 text-sm sm:text-base">Konkurransedyktige priser</h3>
                    <p className="text-gray-600 text-sm sm:text-base">Transparente priser uten skjulte kostnader. Du vet alltid hva du betaler for.</p>
                  </div>
                </div>
              </div>
              <div className="mt-6 sm:mt-8">
                <Link to="/om-oss">
                  <Button className="hover:scale-105 transition-transform duration-300 whitespace-nowrap">
                    Les mer om oss
                  </Button>
                </Link>
              </div>
            </div>
            <div className="relative animate-fade-in-right order-1 lg:order-2">
              <img
                src="https://readdy.ai/api/search-image?query=Professional%20Norwegian%20accountant%20working%20at%20modern%20desk%20with%20financial%20documents%2C%20calculator%20and%20computer%2C%20clean%20office%20environment%20with%20natural%20lighting%2C%20business%20professional%20atmosphere%2C%20focus%20on%20accuracy%20and%20precision%20in%20accounting%20work&width=600&height=400&seq=accounting-professional&orientation=landscape"
                alt="Profesjonell regnskapsfører i Oslo som arbeider med bokføring og regnskapsføring"
                title="Statsautorisert regnskapsfører - Easy Accounting AS"
                className="rounded-lg shadow-lg object-cover w-full h-64 sm:h-80 lg:h-96 hover:scale-105 transition-transform duration-500"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section className="py-16 sm:py-24 bg-blue-50">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="mx-auto max-w-2xl text-center mb-12 sm:mb-16 animate-fade-in-up">
            <h2 className="text-2xl sm:3xl lg:4xl font-bold tracking-tight text-gray-900">
              Hva våre kunder sier
            </h2>
            <p className="mt-3 sm:mt-4 text-base sm:text-lg leading-7 sm:leading-8 text-gray-600">
              Vi er stolte av å levere fremragende service til våre kunder
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 sm:gap-8">
            {testimonials.map((testimonial, index) => (
              <div 
                key={index} 
                className="bg-white rounded-lg p-6 sm:p-8 shadow-sm hover:shadow-md transition-all duration-300 hover:-translate-y-2 animate-fade-in-up"
                style={{ animationDelay: `${index * 0.2}s` }}
              >
                <div className="flex items-center mb-3 sm:mb-4">
                  {[...Array(5)].map((_, i) => (
                    <i key={i} className="ri-star-fill text-yellow-400 animate-pulse text-sm sm:text-base" style={{ animationDelay: `${i * 0.1}s` }}></i>
                  ))}
                </div>
                <p className="text-gray-600 mb-4 sm:mb-6 italic text-sm sm:text-base">
                  "{testimonial.text}"
                </p>
                <div>
                  <div className="font-semibold text-gray-900 text-sm sm:text-base">{testimonial.name}</div>
                  <div className="text-xs sm:text-sm text-gray-500">{testimonial.company}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 sm:py-24 bg-gradient-to-r from-blue-600 to-blue-800 relative overflow-hidden">
        {/* Animated background elements */}
        <div className="absolute inset-0">
          <div className="absolute top-10 left-4 sm:left-10 w-24 sm:w-32 h-24 sm:h-32 bg-white/5 rounded-full animate-spin" style={{ animationDuration: '20s' }}></div>
          <div className="absolute bottom-10 right-4 sm:right-10 w-16 sm:w-24 h-16 sm:h-24 bg-white/5 rounded-full animate-spin" style={{ animationDuration: '15s', animationDirection: 'reverse' }}></div>
        </div>
        
        <div className="relative mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-2xl sm:3xl lg:4xl font-bold tracking-tight text-white mb-4 sm:mb-6 animate-fade-in-up">
            Klar til å forenkle regnskapet ditt?
          </h2>
          <p className="text-lg sm:text-xl text-blue-100 mb-6 sm:mb-8 max-w-2xl mx-auto animate-fade-in-up" style={{ animationDelay: '0.2s' }}>
            Kontakt oss i dag for en uforpliktende samtale om hvordan vi kan hjelpe din bedrift.
          </p>
          <div className="flex flex-col sm:flex-row justify-center items-center gap-3 sm:gap-4 animate-fade-in-up" style={{ animationDelay: '0.4s' }}>
            <Link to="/kontakt" className="w-full sm:w-auto">
              <Button variant="outline" size="lg" className="w-full sm:w-auto text-white border-white hover:bg-white hover:text-blue-600 hover:scale-105 transition-all duration-300 whitespace-nowrap">
                <i className="ri-phone-line mr-2"></i>
                Ring oss nå
              </Button>
            </Link>
            <Link to="/kontakt" className="w-full sm:w-auto">
              <Button variant="outline" size="lg" className="w-full sm:w-auto text-white border-white hover:bg-white hover:text-blue-600 hover:scale-105 transition-all duration-300 whitespace-nowrap">
                <i className="ri-mail-line mr-2"></i>
                Send e-post
              </Button>
            </Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}